import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Check, ScanFace, X } from "lucide-react";

interface Props {
  onCapture: (blob: Blob) => void;
  onCancel: () => void;
  /** What the scan is for, e.g. "Check in". Shown as the heading. */
  title?: string;
}

type CameraState =
  | "initialising"
  | "ready"
  | "scanning"
  | "done"
  | "denied"
  | "error";

/**
 * The check-in scan. A quick camera pass presented as a scan (framed
 * viewport, sweeping line, tick when done) rather than a photo being taken —
 * though a snapshot IS saved with the check-in, and the hint line under the
 * heading says so plainly. No shutter flash, and the captured frame is never
 * shown back to the person.
 */
export default function CameraCapture({ onCapture, onCancel, title }: Props) {
  const { t } = useTranslation();
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const startedRef = useRef(false);
  const blobRef = useRef<Blob | null>(null);
  const timersRef = useRef<number[]>([]);

  const [state, setState] = useState<CameraState>("initialising");
  const [errorMessage, setErrorMessage] = useState<string>("");

  // Start the camera.
  useEffect(() => {
    let cancelled = false;

    async function start() {
      try {
        if (!navigator.mediaDevices?.getUserMedia) {
          throw new Error("Camera API not available in this browser.");
        }
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: false,
          video: {
            facingMode: "user",
            width: { ideal: 1280 },
            height: { ideal: 960 },
          },
        });
        if (cancelled) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          await videoRef.current.play().catch(() => {});
        }
        setState("ready");
      } catch (err) {
        const name = (err as { name?: string }).name;
        if (name === "NotAllowedError" || name === "SecurityError") {
          setState("denied");
          setErrorMessage(t("clock.cameraRequired"));
        } else {
          setState("error");
          setErrorMessage(
            (err as Error)?.message || "Unable to start the camera.",
          );
        }
      }
    }

    void start();

    return () => {
      cancelled = true;
      streamRef.current?.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
      timersRef.current.forEach((id) => window.clearTimeout(id));
    };
  }, []);

  /** Grab the current frame quietly (no flash, nothing shown back). */
  const grabFrame = () => {
    const video = videoRef.current;
    if (!video || video.readyState < 2) {
      setState("error");
      setErrorMessage("Camera did not produce a frame. Please try again.");
      return;
    }
    const canvas = document.createElement("canvas");
    canvas.width = video.videoWidth || 1280;
    canvas.height = video.videoHeight || 960;
    const ctx = canvas.getContext("2d");
    if (!ctx) {
      setState("error");
      setErrorMessage("Canvas not supported in this browser.");
      return;
    }
    // Selfie-mirror so the saved image matches the preview.
    ctx.translate(canvas.width, 0);
    ctx.scale(-1, 1);
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    canvas.toBlob(
      (blob) => {
        if (blob) {
          blobRef.current = blob;
        } else {
          setState("error");
          setErrorMessage("The scan could not be saved. Please try again.");
        }
      },
      "image/jpeg",
      0.9,
    );
  };

  const scan = () => {
    if (startedRef.current) return;
    startedRef.current = true;
    setState("scanning");
    // Capture mid-sweep so the line has visibly passed the face.
    timersRef.current.push(window.setTimeout(grabFrame, 700));
    // One full sweep, then the tick.
    timersRef.current.push(
      window.setTimeout(() => {
        setState((s) => (s === "scanning" ? "done" : s));
      }, 1500),
    );
    // Hand the frame over once the tick has registered.
    timersRef.current.push(
      window.setTimeout(() => {
        if (blobRef.current) onCapture(blobRef.current);
      }, 1950),
    );
  };

  const corners =
    "pointer-events-none absolute h-7 w-7 border-pme-blue/80" as const;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={title ?? t("clock.photoTitle")}
      className="fixed inset-0 z-50 flex items-end justify-center bg-pme-navy/60 p-0 backdrop-blur-sm sm:items-center sm:p-6"
    >
      <div className="w-full max-w-md rounded-t-3xl bg-white p-5 shadow-pop sm:rounded-3xl">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h2 className="text-lg font-bold text-pme-navy">
              {title ?? t("clock.photoTitle")}
            </h2>
            <p className="mt-0.5 text-sm text-pme-muted">
              {t("clock.photoHint")}
            </p>
          </div>
          <button
            type="button"
            onClick={onCancel}
            aria-label={t("common.cancel")}
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-pme-line text-pme-navy hover:bg-pme-sky/50"
          >
            <X size={18} strokeWidth={2} aria-hidden />
          </button>
        </div>

        <div className="relative mt-4 aspect-[4/3] overflow-hidden rounded-2xl bg-pme-navy">
          <video
            ref={videoRef}
            playsInline
            muted
            className={[
              "h-full w-full scale-x-[-1] object-cover transition-opacity duration-300",
              state === "initialising" ? "opacity-0" : "opacity-100",
              state === "done" ? "opacity-40" : "",
            ].join(" ")}
          />

          {/* Scan frame corners */}
          {state !== "denied" && state !== "error" && (
            <>
              <span className={`${corners} left-4 top-4 rounded-tl-xl border-l-2 border-t-2`} />
              <span className={`${corners} right-4 top-4 rounded-tr-xl border-r-2 border-t-2`} />
              <span className={`${corners} bottom-4 left-4 rounded-bl-xl border-b-2 border-l-2`} />
              <span className={`${corners} bottom-4 right-4 rounded-br-xl border-b-2 border-r-2`} />
            </>
          )}

          {/* Sweeping scan line */}
          {state === "scanning" && (
            <span
              aria-hidden
              className="scan-sweep pointer-events-none absolute left-6 right-6 h-0.5 rounded-full bg-emerald-400/90 shadow-[0_0_14px_3px_rgba(52,211,153,0.55)]"
            />
          )}

          {/* Done tick */}
          {state === "done" && (
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-2">
              <span className="flex h-14 w-14 items-center justify-center rounded-full bg-emerald-500 text-white shadow-lg">
                <Check size={30} strokeWidth={2.5} aria-hidden />
              </span>
              <p className="text-sm font-semibold text-white">
                {t("clock.scanDone", "All set")}
              </p>
            </div>
          )}

          {state === "initialising" && (
            <p className="absolute inset-0 flex items-center justify-center text-sm text-white/80">
              {t("clock.cameraStarting")}
            </p>
          )}

          {(state === "denied" || state === "error") && (
            <p className="absolute inset-0 flex items-center justify-center bg-white px-6 text-center text-sm text-pme-ink">
              {errorMessage}
            </p>
          )}
        </div>

        <div className="mt-4 flex gap-3">
          <button
            type="button"
            onClick={onCancel}
            disabled={state === "scanning" || state === "done"}
            className="btn-quiet flex-1 justify-center disabled:opacity-60"
          >
            {t("common.cancel")}
          </button>
          {state !== "denied" && state !== "error" && (
            <button
              type="button"
              onClick={scan}
              disabled={state !== "ready"}
              className="btn-primary flex-[2] justify-center disabled:opacity-60"
            >
              <ScanFace size={18} strokeWidth={2} aria-hidden />
              {state === "scanning" || state === "done"
                ? t("clock.scanning", "Scanning...")
                : t("clock.takePhoto")}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
